import { useJoinCircleMutation, useViewCircleQuery } from "@/api/circles-api";
import UserPlaceholder from "@/assets/icons/user-placeholder.svg?react";
import { Dialog, DialogContent, DialogFooter, DialogHeader } from "@/components/ui/dialog";
import useUserStore from "@/store/userStore";
import { getLoginURL } from "@/utils/auth";
import { useLocation, useRouter } from "@tanstack/react-router";
import { Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import Show from "../Show";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import { Button } from "../ui/button";
import { Separator } from "../ui/separator";
import handsImage from "@/assets/images/hands.jpg";

export default function JoinCircleModal() {
  const { search } = useLocation();
  const circleId = search.circleId;
  const userId = search.userId;
  const router = useRouter();
  const { data: circle, isError } = useViewCircleQuery(circleId!, {
    enabled: !!circleId,
  });

  const [open, setOpen] = useState(false);
  const { t } = useTranslation();
  const { mutate, isPending } = useJoinCircleMutation();
  const { token } = useUserStore();

  useEffect(() => {
    if (circle?.status === 200) {
      setOpen(true);
    } else if (isError) {
      toast.error("Circle not found");
    }
  }, [circle, isError]);

  function handleClose() {
    setOpen(false);
    router.navigate({ search: { circleId: undefined, userId: undefined }, replace: true });
  }

  function handleJoin() {
    if (!token) {
      window.location.href = getLoginURL("");
    } else {
      mutate(
        {
          id: circleId!,
          invited_by_user_id: userId,
        },
        {
          onSuccess: () => {
            toast.success(t("joinCircleModal.success"));
            router.navigate({ to: "/circle/" + circleId, replace: true });
          },
          onError: () => {
            toast.error(t("joinCircleModal.failed"));
          },
        },
      );
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(open) => {
        setOpen(open);
        if (!open) {
          router.navigate({ to: "/", replace: true });
        }
      }}
    >
      <DialogContent variant="bottomOnMobile" className="max-w-3xl md:p-8">
        <DialogHeader className="font-size-h3 mb-2 text-start">
          {t("joinCircleModal.title")}
        </DialogHeader>
        {circle && (
          <div className="flex flex-col gap-4 md:flex-row md:gap-5">
            <img
              src={circle.data.cover_photo?.minio_url ?? handsImage}
              alt={circle.data.title}
              className="aspect-[5/3] w-full rounded-lg object-cover object-center md:w-1/3"
            />
            <div className="flex-1">
              <h4 className="mb-2 font-semibold">{circle.data.title}</h4>
              <dl className="flex flex-col gap-2">
                <div className="flex items-center gap-2">
                  <Avatar className="size-6">
                    <AvatarImage
                      src={circle.data.owner?.image?.minio_url}
                      alt={circle.data.owner?.name}
                    />
                    <AvatarFallback>
                      <UserPlaceholder className="size-full" />
                    </AvatarFallback>
                  </Avatar>
                  <dt>{t("joinCircleModal.createdBy")}</dt>
                  <dd>{circle.data.owner?.name}</dd>
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex items-center">
                    {circle.data.top_contributors.map((user, i) => (
                      <Avatar key={i} className="-ms-2.5 size-6 first-of-type:ms-0">
                        <AvatarImage src={user.image?.minio_url} alt={user.name} />
                        <AvatarFallback>
                          <UserPlaceholder className="size-full" />
                        </AvatarFallback>
                      </Avatar>
                    ))}
                  </div>
                  <dt className="sr-only">{t("joinCircleModal.topContributors")}</dt>
                  <dd>
                    {t("joinCircleModal.contributedCount", {
                      number: circle.data.total_joined_users,
                    })}
                  </dd>
                </div>
                <div className="flex items-center justify-between gap-2">
                  <dt>{t("joinCircleModal.totalDonations")}</dt>
                  <dd>
                    {t("toIntlCurrency", {
                      ...circle.data.total_money,
                      notation: "compact",
                      compactDisplay: "short",
                    })}
                  </dd>
                </div>
                <div className="flex items-center justify-between gap-2">
                  <dt>{t("joinCircleModal.totalPoints")}</dt>
                  <dd>
                    {t("toIntlNumber", {
                      value: circle.data.total_points,
                      notation: "compact",
                      compactDisplay: "short",
                    })}
                  </dd>
                </div>
              </dl>
            </div>
          </div>
        )}
        <Separator className="my-4" />
        <p className="mb-6">{t("joinCircleModal.description")}</p>
        <DialogFooter>
          <Button variant="ghost" className="w-full" size="lg" onClick={handleClose}>
            {t("joinCircleModal.decline")}
          </Button>
          <Button
            className="w-full"
            size="lg"
            onClick={handleJoin}
            disabled={isPending || circle?.data.is_user_joined}
          >
            <Show when={isPending}>
              <Loader2 className="size-4 animate-spin" />
            </Show>
            <Show when={!circle?.data.is_user_joined} fallback={t("joinCircleModal.alreadyJoined")}>
              {t("joinCircleModal.accept")}
            </Show>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
